import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { RAGBadge, RAGScoreCircle } from '../components/common/RAGBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import {
  Shield,
  Plus,
  Calendar,
  TrendingUp,
  Users,
  Clock,
  Activity,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cqcDomains = [
  { key: 'safe', label: 'Safe', description: 'People are protected from abuse and avoidable harm', icon: Shield },
  { key: 'effective', label: 'Effective', description: 'Care achieves good outcomes and promotes quality of life', icon: Activity },
  { key: 'caring', label: 'Caring', description: 'Staff treat people with compassion and dignity', icon: Users },
  { key: 'responsive', label: 'Responsive', description: 'Services meet people\'s needs', icon: Clock },
  { key: 'well_led', label: 'Well-Led', description: 'Leadership promotes high-quality care', icon: TrendingUp },
];

export default function Compliance() {
  const { user, organization } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [latestAssessment, setLatestAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newAssessment, setNewAssessment] = useState({
    assessment_type: 'self_assessment',
    period: 'monthly',
    safe_score: 3,
    effective_score: 3,
    caring_score: 3,
    responsive_score: 3,
    well_led_score: 3,
    notes: '',
    evidence: [],
  });

  useEffect(() => {
    fetchData();
  }, [organization]);

  const fetchData = async () => {
    try {
      const [assessmentsRes, latestRes] = await Promise.all([
        axios.get(`${API_URL}/api/cqc/assessments`, {
          params: { organization_id: organization?.id }
        }),
        axios.get(`${API_URL}/api/cqc/latest`, {
          params: { organization_id: organization?.id }
        })
      ]);
      
      setAssessments(assessmentsRes.data);
      setLatestAssessment(latestRes.data);
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssessment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/cqc/assessments`, {
        ...newAssessment,
        organization_id: organization?.id,
        assessed_by: user?.id,
      });
      toast.success('CQC Assessment created successfully');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating assessment:', error);
      toast.error('Failed to create assessment');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 3.5) return 'text-emerald-600';
    if (score >= 2.5) return 'text-green-600';
    if (score >= 1.5) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score) => {
    if (score === 4) return 'Outstanding';
    if (score === 3) return 'Good';
    if (score === 2) return 'Requires Improvement';
    return 'Inadequate';
  };

  return (
    <div className="min-h-screen" data-testid="compliance-page">
      <Header title="CQC Compliance" />
      
      <div className="p-6 space-y-6">
        {/* Overview Card */}
        <Card className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white" data-testid="compliance-overview">
          <CardContent className="py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <h2 className="text-2xl font-bold mb-2">Overall CQC Rating</h2>
                <p className="text-slate-300 mb-4">Based on the Single Assessment Framework</p>
                <RAGBadge rating={latestAssessment?.overall_rating || 'good'} size="lg" />
              </div>
              
              <div className="flex items-center gap-8">
                <RAGScoreCircle 
                  score={latestAssessment?.overall_score || 0} 
                  rating={latestAssessment?.overall_rating || 'good'}
                  size="lg"
                />
                
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-teal-600 hover:bg-teal-700" data-testid="new-assessment-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New Assessment
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>New CQC Self-Assessment</DialogTitle>
                      <DialogDescription>
                        Rate your organization against the 5 CQC key questions
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateAssessment} className="space-y-6 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Assessment Type</Label>
                          <Select 
                            value={newAssessment.assessment_type}
                            onValueChange={(v) => setNewAssessment(prev => ({ ...prev, assessment_type: v }))}
                          >
                            <SelectTrigger data-testid="assessment-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="self_assessment">Self Assessment</SelectItem>
                              <SelectItem value="internal_audit">Internal Audit</SelectItem>
                              <SelectItem value="external_audit">External Audit</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Period</Label>
                          <Select 
                            value={newAssessment.period}
                            onValueChange={(v) => setNewAssessment(prev => ({ ...prev, period: v }))}
                          >
                            <SelectTrigger data-testid="assessment-period">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="quarterly">Quarterly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-6">
                        {cqcDomains.map((domain) => (
                          <div key={domain.key} className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <domain.icon className="w-5 h-5 text-teal-600" />
                                <Label className="text-base font-medium">{domain.label}</Label>
                              </div>
                              <span className={`text-lg font-bold ${getScoreColor(newAssessment[`${domain.key}_score`])}`}>
                                {newAssessment[`${domain.key}_score`]} - {getScoreLabel(newAssessment[`${domain.key}_score`])}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500">{domain.description}</p>
                            <Slider
                              value={[newAssessment[`${domain.key}_score`]]}
                              onValueChange={(v) => setNewAssessment(prev => ({ ...prev, [`${domain.key}_score`]: v[0] }))}
                              min={1}
                              max={4}
                              step={1}
                              className="w-full"
                              data-testid={`slider-${domain.key}`}
                            />
                            <div className="flex justify-between text-xs text-slate-400">
                              <span>Inadequate</span>
                              <span>Requires Improvement</span>
                              <span>Good</span>
                              <span>Outstanding</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Notes & Evidence</Label>
                        <Textarea
                          value={newAssessment.notes}
                          onChange={(e) => setNewAssessment(prev => ({ ...prev, notes: e.target.value }))}
                          rows={4}
                          placeholder="Add notes about this assessment and any supporting evidence..."
                          data-testid="assessment-notes"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-assessment">
                          Submit Assessment
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Domain Scores */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {cqcDomains.map((domain) => {
            const score = latestAssessment?.[`${domain.key}_score`] || 0;
            return (
              <Card key={domain.key} className="text-center" data-testid={`domain-${domain.key}`}>
                <CardContent className="pt-6">
                  <domain.icon className="w-8 h-8 mx-auto mb-3 text-teal-600" />
                  <h3 className="font-semibold text-slate-900">{domain.label}</h3>
                  <div className="mt-4">
                    <Progress value={score / 4 * 100} className="h-2 mb-2" />
                    <p className={`text-2xl font-bold ${getScoreColor(score)}`}>
                      {score.toFixed(1)}
                    </p>
                    <p className="text-xs text-slate-500">{getScoreLabel(score)}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Assessment History */}
        <Card data-testid="assessment-history">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Assessment History
            </CardTitle>
            <CardDescription>Track your compliance journey over time</CardDescription>
          </CardHeader>
          <CardContent>
            {assessments.length > 0 ? (
              <div className="space-y-4">
                {assessments.map((assessment) => (
                  <div 
                    key={assessment.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        assessment.overall_rating === 'outstanding' || assessment.overall_rating === 'good'
                          ? 'bg-green-100'
                          : assessment.overall_rating === 'requires_improvement'
                            ? 'bg-amber-100'
                            : 'bg-red-100'
                      }`}>
                        {assessment.overall_rating === 'outstanding' || assessment.overall_rating === 'good' ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <AlertCircle className={`w-6 h-6 ${
                            assessment.overall_rating === 'requires_improvement' ? 'text-amber-600' : 'text-red-600'
                          }`} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium capitalize">
                          {assessment.assessment_type.replace('_', ' ')} - {assessment.period}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {new Date(assessment.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <RAGBadge rating={assessment.overall_rating} size="sm" />
                      <p className="text-lg font-bold mt-1">{assessment.overall_score?.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>No assessments yet</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Create Your First Assessment
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
